import { useRef, useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Camera, Sparkles, Download, RotateCcw } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STYLES = [
  "Fade / sfumatura moderna",
  "Taglio corto classico",
  "Buzz cut",
  "Capelli lunghi ordinati",
  "Barba curata e definita",
  "Raso a pelle (pelato)",
];

// "Prova il taglio con l'IA": el cliente se hace un selfie, elige un estilo
// y Gemini (Nano Banana) le genera la foto con ese corte aplicado.
export default function AiTryOn({ onClose }) {
  const [photo, setPhoto] = useState(null); // {file, url} preview local
  const [style, setStyle] = useState("");
  const [customStyle, setCustomStyle] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(""); // data URL de la imagen generada
  const [error, setError] = useState("");
  const [gallery, setGallery] = useState([]); // ejemplos IA generados por el dueño
  const fileRef = useRef(null);

  useEffect(() => {
    fetch(`${BACKEND}/api/ai/style-gallery`)
      .then((r) => r.json())
      .then((d) => setGallery(d.items || []))
      .catch(() => {});
  }, []);

  const chosenStyle = customStyle.trim() || style;

  // Franja de inspiración: ejemplos IA de cada estilo (tocar = elegir estilo)
  const galleryStrip = gallery.length > 0 && (
    <div className="flex flex-col gap-1.5">
      <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500">
        Ispirati ✨
      </span>
      <div data-testid="ai-gallery-strip" className="flex gap-2 overflow-x-auto pb-1">
        {gallery.map((g) => (
          <button
            key={g.style}
            data-testid={`ai-gallery-${g.style.slice(0, 10)}`}
            onClick={() => {
              setStyle(g.style);
              setCustomStyle("");
            }}
            className={`w-24 shrink-0 overflow-hidden rounded-xl border-2 transition-colors duration-150 ${
              style === g.style && !customStyle
                ? "border-amber-400"
                : "border-white/10 hover:border-white/30"
            }`}
          >
            <img
              src={`${BACKEND}${g.url}`}
              alt={g.style}
              className="h-20 w-24 object-cover"
              loading="lazy"
            />
            <span className="block truncate px-1 py-1 text-center text-[9px] font-bold text-zinc-300">
              {g.style}
            </span>
          </button>
        ))}
      </div>
    </div>
  );

  const pickPhoto = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setResult("");
    setError("");
    setPhoto({ file: f, url: URL.createObjectURL(f) });
  };

  const generate = async () => {
    if (busy || !photo || !chosenStyle) return;
    setBusy(true);
    setError("");
    setResult("");
    try {
      const fd = new FormData();
      fd.append("file", photo.file);
      fd.append("style", chosenStyle);
      const res = await fetch(`${BACKEND}/api/ai/haircut`, { method: "POST", body: fd });
      const d = await res.json();
      if (!res.ok) {
        setError(d.detail || "Errore, riprova.");
      } else {
        setResult(d.image);
      }
    } catch {
      setError("Connessione assente. Controlla la rete e riprova.");
    }
    setBusy(false);
  };

  const reset = () => {
    setPhoto(null);
    setResult("");
    setError("");
    setStyle("");
    setCustomStyle("");
  };

  return (
    <motion.div
      data-testid="ai-tryon-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-[80] flex items-end justify-center bg-zinc-950/70 backdrop-blur-sm"
    >
      <motion.div
        initial={{ y: 90 }}
        animate={{ y: 0 }}
        exit={{ y: 90 }}
        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="mx-auto flex max-h-[92dvh] w-full max-w-md flex-col overflow-hidden rounded-t-3xl border-t border-amber-400/30 bg-zinc-900"
      >
        <div className="flex items-center justify-between px-6 pt-5">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">
              Novità con IA
            </p>
            <h3 className="font-display text-xl font-bold text-white">
              Prova il tuo taglio ✨
            </h3>
          </div>
          <button
            data-testid="ai-tryon-close"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-white/5 text-zinc-400 transition-colors hover:bg-white/10 hover:text-white"
          >
            <X size={17} />
          </button>
        </div>

        <div className="flex flex-col gap-4 overflow-y-auto px-6 py-5 pb-10">
          {galleryStrip}
          {!photo && (
            <button
              data-testid="ai-photo-btn"
              onClick={() => fileRef.current?.click()}
              className="flex flex-col items-center gap-3 rounded-2xl border-2 border-dashed border-amber-400/40 bg-zinc-950/60 px-4 py-10 text-center transition-colors hover:border-amber-400/70"
            >
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-400/15 text-amber-400">
                <Camera size={26} />
              </span>
              <span className="text-sm font-bold text-white">Fatti un selfie</span>
              <span className="text-xs leading-relaxed text-zinc-500">
                Viso frontale, buona luce: l&apos;IA ti mostra come stai con il taglio scelto
              </span>
            </button>
          )}
          <input
            ref={fileRef}
            data-testid="ai-photo-input"
            type="file"
            accept="image/*"
            capture="user"
            onChange={pickPhoto}
            className="hidden"
          />

          {photo && !result && (
            <>
              <div className="relative mx-auto w-full max-w-[240px] overflow-hidden rounded-2xl border border-white/10">
                <img
                  data-testid="ai-photo-preview"
                  src={photo.url}
                  alt="Il tuo selfie"
                  className="w-full object-cover"
                />
                <button
                  data-testid="ai-photo-retake"
                  onClick={() => fileRef.current?.click()}
                  className="absolute bottom-2 right-2 flex items-center gap-1 rounded-full bg-zinc-950/80 px-3 py-1.5 text-[10px] font-bold uppercase text-zinc-300 backdrop-blur transition-colors hover:text-white"
                >
                  <RotateCcw size={11} /> Cambia
                </button>
              </div>

              <div className="flex flex-col gap-2">
                <span className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500">
                  <Sparkles size={12} className="text-amber-400" /> Scegli il taglio
                </span>
                <div data-testid="ai-styles" className="flex flex-wrap gap-2">
                  {STYLES.map((s) => (
                    <button
                      key={s}
                      data-testid={`ai-style-${s.slice(0, 12)}`}
                      onClick={() => {
                        setStyle(s);
                        setCustomStyle("");
                      }}
                      className={`rounded-xl border px-3 py-2 text-xs font-bold transition-colors duration-150 ${
                        style === s && !customStyle
                          ? "border-amber-400/60 bg-amber-400/15 text-amber-300"
                          : "border-white/10 bg-zinc-950 text-zinc-300 hover:border-white/25"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
                <input
                  data-testid="ai-style-custom"
                  value={customStyle}
                  onChange={(e) => setCustomStyle(e.target.value)}
                  placeholder="O scrivi il tuo stile… (es. fade con riga)"
                  maxLength={120}
                  className="h-11 rounded-xl border border-white/10 bg-zinc-950 px-3 text-sm text-white outline-none placeholder:text-zinc-600 focus:border-amber-400/50"
                />
              </div>

              <button
                data-testid="ai-generate-btn"
                onClick={generate}
                disabled={busy || !chosenStyle}
                className="rounded-2xl bg-amber-400 py-4 text-sm font-extrabold uppercase tracking-widest text-zinc-950 shadow-[0_0_24px_rgba(251,191,36,0.3)] transition-[background-color,transform] duration-200 hover:bg-amber-300 active:scale-[0.97] disabled:opacity-40 disabled:shadow-none"
              >
                {busy ? "L'IA sta lavorando… (20-40 sec)" : "Genera la mia foto ✨"}
              </button>
            </>
          )}

          {error && (
            <p
              data-testid="ai-error"
              className="rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2.5 text-xs font-semibold text-red-300"
            >
              {error}
            </p>
          )}

          {result && (
            <div className="flex flex-col items-center gap-4">
              <img
                data-testid="ai-result-img"
                src={result}
                alt="Il tuo nuovo taglio"
                className="w-full max-w-[280px] rounded-2xl border border-amber-400/30 object-cover shadow-[0_8px_40px_rgba(251,191,36,0.2)]"
              />
              <p className="text-center text-xs leading-relaxed text-zinc-400">
                Ecco come staresti con: <span className="font-bold text-amber-300">{chosenStyle}</span>
              </p>
              <div className="flex w-full gap-2">
                <a
                  data-testid="ai-download-btn"
                  href={result}
                  download="mio-taglio-barber-latin.png"
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-emerald-500 py-3 text-[11px] font-extrabold uppercase tracking-widest text-white transition-colors hover:bg-emerald-400"
                >
                  <Download size={14} /> Scarica
                </a>
                <button
                  data-testid="ai-retry-btn"
                  onClick={reset}
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-white/15 py-3 text-[11px] font-extrabold uppercase tracking-widest text-zinc-300 transition-colors hover:border-white/30"
                >
                  <RotateCcw size={14} /> Riprova
                </button>
              </div>
            </div>
          )}

          <p className="text-center text-[10px] leading-relaxed text-zinc-600">
            La foto serve solo per la prova e non viene salvata nel salone. Limite di prove al giorno.
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
}
